<template lang="pug">
v-menu(v-model="showMenu" offset-y rounded="lg" :close-on-content-click="false" max-height="360" min-width="456" max-width="456" nudge-bottom="4")
  template(v-slot:activator="{ on }")
    v-card.round-xl.px-3(v-on="on" outlined link height="36")
      div.d-flex.align-center.fill-height
        v-icon.mr-2(v-if="urgentCount" size="16" color="danger500") $yzi-alert-circle-solid
        v-icon.mr-2(v-else size="16" color="neutral700") $yzi-users-03
        div.text-sm.font-medium {{ urgentCount ? $tc('urgent_patients', urgentCount) : $tc('patients_waiting', count)}}
        v-icon.ml-2.mr-n1(size="23" color="neutral700") mdi-chevron-down
  .white
    .menu-header.white
      div.px-4.py-3
        v-tabs(v-model="tabs" hide-slider height="24" @change="fetchPatients()")
          v-tab
            div.text-xs.font-medium.normal-case Your patient
          v-tab
            div.text-xs.font-medium.normal-case All in clinic
      v-divider
    div
      div.pb-3.white(v-if="isFetching")
        v-progress-linear(width="3" rounded color="primary" indeterminate)
      template(v-else)
        template(v-for="(patient, idx) in patients")
          div.px-4.py-3.clickable.hover-darken(no-gutters :key="`pt-${idx}`" @click="onViewOnePatient(patient.pid)")
            div.d-flex.align-center
              div.text-sm.neutral700--text {{ patient.patient.name }}
              v-spacer
              div.pl-2(v-if="patient.urgent")
                y-chip(label="Urgent" color="red" size="xs")
              div.pl-2
                div.custom-chip.text-xs.font-medium {{ patient.queue_no }}
              div.pl-2
                y-chip.no-wrap(:label="getDuration(patient)" size="xs" chip-class="pa-1" outlined)
                  template(#prepend)
                    v-icon.mr-1(size=16) mdi-clock-outline
          v-divider
        div.d-flex.justify-center.px-4.py-2
          y-button(label="View all waiting patients" size="xs" underline append-icon="$yzi-arrow-narrow-up-right" icon-size="12" icon-color="neutral700" button-class="px-0" @click="viewAllWaitingPatients")
            
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters, mapActions, mapState } from 'vuex'
export default {
  data () {
    return {
      isFetching: false,
      showMenu: false,
      currentTime: null,
      tabs: 0,
      count: 0,
      urgentCount: 0,
      patients: []
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      consultation: 'treatment/getConsultation'
    }),
    inQueue () {
      return this.consultation.in_queue
    },
    getBaseConsultationUrl() {
      if (this.$subdomain) {
        return `${this.$subdomain}.${process.env.VUE_APP_SHORT_URL_DOMAIN}/admin/clinic/consultation`
      } else {
        return `${window.location.origin}/admin/clinic/consultation`
      }
    },
  },
  watch: {
    showMenu (val) {
      if (val) {
        this.fetchPatients()
      }
    }
  },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.currentTime = new Date().toISOString()
      if (this.inQueue && this.inQueue.total) this.count = this.inQueue.total
      if (this.inQueue && this.inQueue.urgent) this.urgentCount = this.inQueue.urgent
    },
    fetchPatients () {
      this.updateTime()
      this.isFetching = true
      let params = {
        fields: 'pid,patient,urgent,waiting_at,status,queue_no',
        codes: '1,2',
        status: 'waiting',
        ordering: 'urgent',
        page_size: -1
      }
      if (!this.tabs) {
        params.providers = this.member.provider
      }
      this.$axios.get(`/clinics/${this.company.slug}/registrations`, { params })
        .then(resp => {
          this.patients = []
          this.count = 0
          this.urgentCount = 0
          if (resp.data && resp.data.results) {
            this.patients = resp.data.results
            this.count = resp.data.count
            this.urgentCount = resp.data.results.filter(f => f.urgent).length
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    updateTime () {
      this.currentTime = new Date().toISOString()
    },
    getDuration (item) {
      const end = DateTime.fromISO(this.currentTime)
      let startTime
      switch (item.status) {
        case 'waiting':
          startTime = item.waiting_at
          break;
        case 'on_hold':
          startTime = item.on_hold_at
          break;
        default:
          startTime = new Date()
          break;
      }
      const start = DateTime.fromISO(startTime)
      let diff = end.diff(start, ['hours', 'minutes'])
      let hours = Math.floor(diff.toObject().hours)
      let minutes = Math.floor(diff.toObject().minutes)
      if (hours) {
        return `${hours} ${this.$options.filters.pluralize('hour', { count:hours })}  ${minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
      }
      return `${minutes < 1 ? '<1' : minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
    },
    onViewOnePatient(leadPid) {
      const url = `${this.getBaseConsultationUrl}/${leadPid}`
      window.open(url)
    },
    viewAllWaitingPatients() {
      const url = `${this.getBaseConsultationUrl}?tab=waiting`
      window.open(url)
    },
  }
}
</script>

<style lang="scss" scoped>
.menu-header {
  top: 0;
  position: sticky;
  z-index: 1;
}
.v-card--link:focus:before {
  opacity: 0;
}
.v-tab--active {
  background-color: var(--v-neutral300);
  border-radius: 6px;
}
.hover-darken {
  transition: background-color 0.3s ease;
}
.hover-darken:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
.custom-chip {
  padding: 2px 5px;
  border: solid 1px var(--v-neutral400);
  border-radius: 6px;
}
</style>

<i18n>
{
  "en": {
    "patients_waiting" : "1 patient waiting | {n} patients waiting",
    "urgent_patients" : "1 urgent patient | {n} urgent patients"
  }
}
</i18n>